import { ContainerShape, InnerShape, InnerShape2 } from "./Shape"
import { motion } from "framer-motion"
import { useDisconnect } from "wagmi"
import { profileCache } from "~/blinko/components/ProfileSlot"
import { useAccount } from "wagmi"
import { useSound } from "./SoundHandler"
import { atom, useAtom } from "jotai"
import { icons } from "../assets/icons/icons"
import { ZERO_ADDRESS } from "~/core/constants"
import { useModal } from "~/core/contexts/ModalContext"
import { useToast } from "~/core/contexts/ToastContext"
import { useAllBalance } from "~/core/hooks/useAllBalance"
import { useEffect } from "react"
import { ReferralModal } from "../modals/referral.modal"
import { useRef } from "react"
import { AnimatePresence } from "framer-motion"
import { TwitterSlot } from "~/blinko/components/TwitterSlot"
import { useResolution } from "../hooks/useResolution"
import { HistoryModal } from '../modals/history.modal'
import { useAddFunds } from "~/core/providers/AddFundsProvider"
import { SelfExclusionModal } from "../modals/self.exclusion.modal"
import WalletSlot from "~/core/components/WalletSlot"
import { AchievementsModal } from "../modals/achievements.modal"

const atomShow = atom(false)

export const useWalletMenu = () => {
  const [, setShowPopover] = useAtom(atomShow)

  const toggle = () => {
    setShowPopover(x => !x)
  }

  return { toggle }
}

export const WalletMenu = () => {
  const { address } = useAccount()
  const toast = useToast()
  const { getBalance, mutate: mutateAllBalance } = useAllBalance()
  const { connectors, disconnect } = useDisconnect()
  const { showModal } = useModal()
  const sound = useSound()
  const { isMobile } = useResolution()
  const { addFunds } = useAddFunds()

  const [showPopover, setShowPopover] = useAtom(atomShow)

  const onCopy = () => {
    navigator.clipboard.writeText(address!)
    toast.success('Copied to clipboard')
    sound.play('click');
  }

  const onReferral = () => {
    showModal(hide => <ReferralModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
  }

  const onHistory = () => {
    showModal(hide => <HistoryModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
  }

  const onAchievement = () => {
    showModal(hide => <AchievementsModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
  }

  const onDisconnect = () => {
    disconnect()
    connectors.forEach(connector => {
      connector.disconnect()
    })
    localStorage.clear()
    sound.play('click');
  }

  const onAddFunds = async () => {
    const profile = await profileCache.get(address!)
    addFunds({
      name: profile.name || `blinko${address!.slice(2, 6)}`,
      address: profile.address,
    }, () => {
      mutateAllBalance()
    })
  }

  const onSelfExclusion = () => {
    showModal(hide => (
      <SelfExclusionModal 
        onDismiss={hide}
      />
    ), { hideOnEscapeKey: true });
    sound.play('click');
  }

  const handleOutsideClickRef = useRef<HTMLDivElement>(null)
  const handleOutsideClick = (event: PointerEvent) => {
    if (handleOutsideClickRef.current && !handleOutsideClickRef.current.contains(event.target as Node)) {
      setShowPopover(false)
      sound.play('click');
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', handleOutsideClick)
    return () => document.removeEventListener('pointerdown', handleOutsideClick)
  }, [])

  const show = showPopover || isMobile

  if (!address) {
    return (
      <AnimatePresence>
        {show ? (
          <motion.div
            ref={handleOutsideClickRef}
            key="popover"
            initial={isMobile ? { } : { y: '-20%', opacity: 0 }}
            animate={isMobile ? { } : { y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          >
            <WalletSlot>
              {({ connect }) => (
                <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
                  <div 
                    ref={handleOutsideClickRef}
                    className="flex flex-col gap-1 p-3 md:w-[220px]"
                  >
                    <p className="px-2 mb-1.5 font-blinko text-[10px] text-white">
                      Connect Wallet
                    </p>

                    <div className="flex flex-col gap-2">
                      <button onClick={connect}>
                        <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                          <div className="relative p-2.5">
                            <img src={icons.wallet} className='w-5 h-5' />
                            <div className='absolute inset-0 flex items-center justify-center'>
                              <p className="text-sm font-blinko text-white">Sign In</p>
                            </div>
                          </div>
                        </InnerShape>
                      </button>
                    </div>
                  </div>
                </ContainerShape>
              )}
            </WalletSlot>
          </motion.div>
        ) : (
          <>
          </>
        )}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          ref={handleOutsideClickRef}
          key="popover"
          initial={isMobile ? { } : { y: '-20%', opacity: 0 }}
          animate={isMobile ? { } : { y: 0, opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 500 }}
        >
          <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
            <div 
              ref={handleOutsideClickRef}
              className="flex flex-col gap-1 p-3 md:w-[220px]"
            >
              <p className="px-2 mb-1.5 font-blinko text-[10px] text-white">
                Connected Wallet
              </p>

              <div className="flex flex-col gap-2 rounded-lg">
                <InnerShape2 cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <button 
                    className="flex items-center gap-1 p-2 w-full"
                    onClick={onCopy}
                  >
                    <img src={icons.wallet} className='w-4 h-4 opacity-50' />
                    <p className="text-sm text-white">{address!.slice(0, 4)}..{address!.slice(-4)}</p>
                    <img src={icons.copy} className='ml-auto w-4 h-4 opacity-50' />
                  </button>
                </InnerShape2>
                

                <InnerShape2 cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="flex items-center gap-1 p-2">
                    <img src={icons.currencyEthereum} className='w-4 h-4 opacity-50' />
                    <p className="text-sm text-white">{getBalance(ZERO_ADDRESS).formattedBalance}</p>
                    <p className="ml-auto text-sm text-white/50">ETH</p>
                  </div>
                </InnerShape2>
                
                <button onClick={onAddFunds}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.cashBanknote} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">Add Funds</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>

                <button onClick={onSelfExclusion}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.coinOff} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">Self Exclusion</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>

                <button onClick={onHistory}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.history} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">History</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>

                 <button onClick={onAchievement}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.rocket} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">Achievement</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>

                <button onClick={onReferral}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.userPlus} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">Referrals</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>
                
                <TwitterSlot address={address!}>
                  {({ connected, connect }) => (
                    <button onClick={connect}>
                      <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                        <div className="relative p-2.5">
                          <img src={icons.twitterX} className='w-5 h-5' />
                          <div className='absolute inset-0 flex items-center justify-center'>
                            <p className="font-blinko text-sm text-white">{connected ? 'Relink Account' : 'Link Account'}</p>
                          </div>
                        </div>
                      </InnerShape>
                    </button>
                  )}
                </TwitterSlot>

                <button onClick={onDisconnect}>
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.arrowsLogout} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="font-blinko text-sm text-white">Disconnect</p>
                      </div>
                    </div>
                  </InnerShape>
                </button>

              </div>
            </div>
          </ContainerShape>
        </motion.div>
      ) : (
        <>
        </>
      )}
    </AnimatePresence>
  )
}
